/**
 * The front-panel rotary knob.
 *
 * One machined cap, two behaviours, because a real set has exactly two kinds of knob on it. The
 * tuning knob is an *encoder*: endless, with no end-stops, where turning is worth a relative
 * change and the exact number lives in the readout above it. Everything else is a *pot*: a
 * bounded 270° sweep where the angle of the cap is the value, the way a clarity or an
 * interference control has always worked.
 *
 * Input (desktop-first, matching the dial this replaces):
 *   • drag the rim — turn the cap; drag the hub — a straight sweep; hold Shift for coarse
 *   • wheel — fine; Shift+wheel steps coarse
 *   • keyboard — ←/→ fine, Shift+←/→ coarse (encoder), PageUp/PageDown jump, Home/End edges
 *
 * Accessibility: the container is a real `role="slider"` with live `aria-valuenow`/`aria-valuetext`,
 * so the value is announced as it changes and the whole control is keyboard-operable. The canvas
 * cap is decorative and hidden from assistive tech.
 */
import { h } from '../dom.js';

/* ---- geometry and steps (pure — this is the half the tests hold on to) --------------------- */

/** A bounded pot sweeps 270°, −135° to +135°. Past that is the end-stop. */
export const POT_SWEEP_DEG = 270;

/** How far the tuning cap turns per kHz — and so what a degree of turn is worth. */
export const ENCODER_DEG_PER_KHZ = 1;

/** kHz across the width of the knob on a straight drag. The dial's sweep rate, kept. */
export const ENCODER_WINDOW_KHZ = 620;

export const FINE_STEP_KHZ = 1;
export const COARSE_STEP_KHZ = 25;
export const JUMP_STEP_KHZ = 200;
export const SHIFT_DRAG_MULTIPLIER = 5;

/** Pots step in twentieths: the same resolution the range inputs they replace had. */
export const POT_STEP = 0.05;
export const POT_JUMP = 0.25;

const COMMIT_DELAY_MS = 400;
const IDLE_DELAY_MS = 220;
/** How much of the knob's box the cap fills; the rest is the engraved scale around it. */
const CAP_FRACTION = 0.76;
/** Inside this fraction of the cap the knob is a hub, not a rim — a press there drags straight. */
const HUB_FRACTION = 0.32;
/** Below this the pointer is too close to the spindle for an angle to mean anything. */
const MIN_TURN_RADIUS_PX = 10;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Pot cap angle (degrees, 0 = straight up, clockwise positive) → 0–1. */
export function potAngleToValue(degrees: number): number {
  return clamp((degrees + POT_SWEEP_DEG / 2) / POT_SWEEP_DEG, 0, 1);
}

/** 0–1 → pot cap angle in degrees. The inverse of `potAngleToValue` inside the end-stops. */
export function potValueToAngle(value: number): number {
  return clamp(value, 0, 1) * POT_SWEEP_DEG - POT_SWEEP_DEG / 2;
}

/** A pot lands on twentieths, so a keyboard walk never drifts into 0.30000000000000004. */
export function quantisePot(value: number): number {
  return clamp(Math.round(value * 100) / 100, 0, 1);
}

/** Angle of a pointer offset from the cap's centre: 0° is straight up, clockwise is positive. */
export function pointerAngleDeg(dx: number, dy: number): number {
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
}

/** The short way round between two angles, in (−180, 180]. Keeps a turn past 12 o'clock honest. */
export function shortestAngleDelta(fromDeg: number, toDeg: number): number {
  let delta = (toDeg - fromDeg) % 360;
  if (delta > 180) delta -= 360;
  if (delta <= -180) delta += 360;
  return delta;
}

/**
 * Split a fractional change into whole units plus the remainder to carry.
 *
 * Without the carry a slow drag is a stream of sub-unit moves that each truncate to nothing, and
 * the knob simply does not turn — which is exactly the fine control the tuning DSP needs most.
 */
export function accumulate(delta: number, residue: number): { whole: number; residue: number } {
  const total = delta + residue;
  const whole = Math.trunc(total);
  return { whole, residue: total - whole };
}

/** kHz per pixel of straight drag across a knob of this width. Shift sweeps five times as fast. */
export function encoderKhzPerPixel(width: number, shiftKey: boolean): number {
  return (ENCODER_WINDOW_KHZ / Math.max(width, 1)) * (shiftKey ? SHIFT_DRAG_MULTIPLIER : 1);
}

export interface KeySpec {
  min: number;
  max: number;
  fine: number;
  /** The Shift step. Equal to `fine` where Shift means nothing (pots). */
  coarse: number;
  jump: number;
}

/** The keyboard map, clamped. Returns null for a key the knob does not claim. */
export function nextFromKey(
  key: string,
  shiftKey: boolean,
  value: number,
  spec: KeySpec,
): number | null {
  const step = shiftKey ? spec.coarse : spec.fine;

  switch (key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      return clamp(value - step, spec.min, spec.max);
    case 'ArrowRight':
    case 'ArrowUp':
      return clamp(value + step, spec.min, spec.max);
    case 'PageDown':
      return clamp(value - spec.jump, spec.min, spec.max);
    case 'PageUp':
      return clamp(value + spec.jump, spec.min, spec.max);
    case 'Home':
      return spec.min;
    case 'End':
      return spec.max;
    default:
      return null;
  }
}

export const ENCODER_KEYS = (min: number, max: number): KeySpec => ({
  min,
  max,
  fine: FINE_STEP_KHZ,
  coarse: COARSE_STEP_KHZ,
  jump: JUMP_STEP_KHZ,
});

/** Arrows are ±5% on a pot whether or not Shift is down; the sweep is too short to want more. */
export const POT_KEYS: KeySpec = {
  min: 0,
  max: 1,
  fine: POT_STEP,
  coarse: POT_STEP,
  jump: POT_JUMP,
};

/* ---- the component ------------------------------------------------------------------------ */

export interface EncoderKnobOptions {
  mode: 'encoder';
  minKhz: number;
  maxKhz: number;
  valueKhz: number;
  label: string;
  /** Fired continuously while turning. */
  onInput: (khz: number) => void;
  /** Fired when the player stops moving (pointer up, key up, wheel settle). */
  onCommit?: (khz: number) => void;
}

export interface PotKnobOptions {
  mode: 'pot';
  /** 0–1. */
  value: number;
  label: string;
  onInput: (value: number) => void;
  onCommit?: (value: number) => void;
}

export type KnobOptions = EncoderKnobOptions | PotKnobOptions;

export interface Knob {
  element: HTMLElement;
  setValue(value: number): void;
  /** 0–1 proximity to a transmission; drives the lock indicator. Encoder knobs only. */
  setStrength?(strength: number): void;
  dispose(): void;
}

export function createKnob(options: KnobOptions): Knob {
  const { label, onInput, onCommit } = options;
  const isEncoder = options.mode === 'encoder';
  const min = options.mode === 'encoder' ? options.minKhz : 0;
  const max = options.mode === 'encoder' ? options.maxKhz : 1;
  const keys: KeySpec = options.mode === 'encoder' ? ENCODER_KEYS(min, max) : POT_KEYS;

  let value =
    options.mode === 'encoder'
      ? clamp(Math.round(options.valueKhz), min, max)
      : quantisePot(options.value);
  let strength = 0;
  let locked = false;

  const canvas = h('canvas', { class: 'nb-knob__cap', 'aria-hidden': 'true' });
  const lock = isEncoder ? h('span', { class: 'nb-knob__lock', 'aria-hidden': 'true' }) : null;

  const element = h(
    'div',
    {
      class: `nb-knob nb-knob--${options.mode}`,
      role: 'slider',
      tabindex: '0',
      'aria-label': label,
      'aria-valuemin': String(ariaValue(min)),
      'aria-valuemax': String(ariaValue(max)),
      'aria-valuenow': String(ariaValue(value)),
      'aria-valuetext': valueText(value),
      'aria-orientation': 'horizontal',
    },
    canvas,
    ...(lock ? [lock] : []),
  );

  let frame: number | null = null;
  const scheduleDraw = (): void => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      draw();
    });
  };

  let commitTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleCommit = (): void => {
    if (!onCommit) return;
    if (commitTimer) clearTimeout(commitTimer);
    commitTimer = setTimeout(() => onCommit(value), COMMIT_DELAY_MS);
  };

  const settle = (next: number): number =>
    isEncoder ? clamp(Math.round(next), min, max) : quantisePot(next);

  const apply = (next: number, notify = true): void => {
    const settled = settle(next);
    if (settled === value) return;
    value = settled;
    element.setAttribute('aria-valuenow', String(ariaValue(value)));
    element.setAttribute('aria-valuetext', valueText(value));
    scheduleDraw();
    if (notify) {
      onInput(value);
      scheduleCommit();
    }
  };

  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleIdle = (): void => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => element.classList.remove('is-turning'), IDLE_DELAY_MS);
  };

  // --- pointer: turn the cap ---------------------------------------------------------------
  let dragging = false;
  let turning = false; // rim grab (circular) rather than hub grab (straight)
  let lastX = 0;
  let lastY = 0;
  let lastAngle = 0;
  let residue = 0;

  const centre = (): { x: number; y: number } => {
    const box = element.getBoundingClientRect();
    return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    const { x, y } = centre();
    const dx = event.clientX - x;
    const dy = event.clientY - y;
    const radius = Math.hypot(dx, dy);
    const capRadius = (Math.min(element.clientWidth, element.clientHeight) / 2) * CAP_FRACTION;

    dragging = true;
    // The rim turns and the hub sweeps: grabbing the middle of a knob and pulling is how a
    // player who does not want to describe a circle with the mouse still gets across the band.
    turning = radius > capRadius * HUB_FRACTION && radius > MIN_TURN_RADIUS_PX;
    lastAngle = pointerAngleDeg(dx, dy);
    lastX = event.clientX;
    lastY = event.clientY;
    residue = 0;
    element.setPointerCapture(event.pointerId);
    element.classList.add('is-turning');
    element.focus({ preventScroll: true });
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!dragging) return;

    let delta: number;
    if (turning) {
      const { x, y } = centre();
      const angle = pointerAngleDeg(event.clientX - x, event.clientY - y);
      const turned = shortestAngleDelta(lastAngle, angle);
      lastAngle = angle;
      delta = isEncoder
        ? turned / ENCODER_DEG_PER_KHZ
        : potAngleToValue(potValueToAngle(value) + turned) - value;
      if (isEncoder && event.shiftKey) delta *= SHIFT_DRAG_MULTIPLIER;
    } else {
      // Right and up both turn it up, the way a thumb rolling a knob does.
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      delta = isEncoder
        ? (dx - dy) * encoderKhzPerPixel(element.clientWidth, event.shiftKey)
        : (dx - dy) / (POT_SWEEP_DEG * 0.7);
    }
    lastX = event.clientX;
    lastY = event.clientY;

    if (isEncoder) {
      const carried = accumulate(delta, residue);
      residue = carried.residue;
      if (carried.whole !== 0) apply(value + carried.whole);
    } else {
      apply(value + delta);
    }
  };

  const endDrag = (event: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    element.classList.remove('is-turning');
    if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
    onCommit?.(value);
  };

  // --- wheel ---------------------------------------------------------------------------------
  const onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const step = event.shiftKey ? keys.coarse : keys.fine;
    apply(value + Math.sign(event.deltaY || event.deltaX) * step);
    element.classList.add('is-turning');
    scheduleIdle();
  };

  // --- keyboard ------------------------------------------------------------------------------
  const onKeyDown = (event: KeyboardEvent): void => {
    const next = nextFromKey(event.key, event.shiftKey, value, keys);
    if (next === null) return;
    event.preventDefault();
    apply(next);
    element.classList.add('is-turning');
    scheduleIdle();
  };

  const onKeyUp = (): void => onCommit?.(value);

  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('pointermove', onPointerMove);
  element.addEventListener('pointerup', endDrag);
  element.addEventListener('pointercancel', endDrag);
  element.addEventListener('wheel', onWheel, { passive: false });
  element.addEventListener('keydown', onKeyDown);
  element.addEventListener('keyup', onKeyUp);

  // --- rendering ------------------------------------------------------------------------------
  const style = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string): string =>
    style.getPropertyValue(name).trim() || fallback;
  const colours = {
    tick: token('--nb-ghost-40', 'rgba(255,255,255,0.42)'),
    tickFaint: token('--nb-ghost-20', 'rgba(255,255,255,0.2)'),
    hub: token('--nb-ghost-12', 'rgba(255,255,255,0.12)'),
    line: token('--nb-line', 'rgba(140,170,210,0.16)'),
    signal: token('--nb-signal', '#5c9ce6'),
    signalStrong: token('--nb-signal-strong', '#8fc2ff'),
    metalHi: token('--nb-metal-hi', '#2a3446'),
    metalLo: token('--nb-metal-lo', '#0a0e15'),
  };

  function draw(): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = element.clientWidth;
    const height = element.clientHeight;
    if (width === 0 || height === 0) return;

    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2;
    const outer = Math.min(width, height) / 2 - 1;
    const capR = outer * CAP_FRACTION;
    const angle = isEncoder ? value * ENCODER_DEG_PER_KHZ : potValueToAngle(value);

    const toRad = (deg: number): number => ((deg - 90) * Math.PI) / 180;
    const point = (radius: number, deg: number): [number, number] => {
      const rad = toRad(deg);
      return [cx + Math.cos(rad) * radius, cy + Math.sin(rad) * radius];
    };
    const spoke = (from: number, to: number, deg: number): void => {
      const [x1, y1] = point(from, deg);
      const [x2, y2] = point(to, deg);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };

    ctx.lineCap = 'butt';

    // The engraved scale on the panel around the knob — fixed, because the panel does not turn.
    const tickCount = isEncoder ? 24 : 11;
    ctx.lineWidth = 1;
    for (let i = 0; i < tickCount; i += 1) {
      const deg = isEncoder
        ? (360 / tickCount) * i
        : -POT_SWEEP_DEG / 2 + (POT_SWEEP_DEG / (tickCount - 1)) * i;
      // Four cardinal majors on the endless ring; both end-stops and the centre on a pot.
      const major = isEncoder ? i % 6 === 0 : i % 5 === 0;
      ctx.strokeStyle = major ? colours.tick : colours.tickFaint;
      spoke(outer - (major ? 8 : 5), outer, deg);
    }

    // A pot says where it stands even at a glance, so the sweep so far is drawn in.
    if (!isEncoder && value > 0) {
      ctx.strokeStyle = colours.signal;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, outer - 11, toRad(-POT_SWEEP_DEG / 2), toRad(angle));
      ctx.stroke();
    }

    // The bezel: a lit top edge and a dark underside, which is all it takes to read as turned metal.
    const bezel = ctx.createLinearGradient(cx, cy - capR - 4, cx, cy + capR + 4);
    bezel.addColorStop(0, colours.metalHi);
    bezel.addColorStop(1, colours.metalLo);
    ctx.fillStyle = bezel;
    ctx.beginPath();
    ctx.arc(cx, cy, capR + 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = colours.line;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Knurling. It rotates with the cap, so an endless encoder still shows that it moved.
    ctx.strokeStyle = colours.tickFaint;
    for (let i = 0; i < 36; i += 1) spoke(capR + 0.5, capR + 4, angle + i * 10);

    const face = ctx.createRadialGradient(
      cx - capR * 0.35,
      cy - capR * 0.4,
      capR * 0.1,
      cx,
      cy,
      capR,
    );
    face.addColorStop(0, colours.metalHi);
    face.addColorStop(1, colours.metalLo);
    ctx.fillStyle = face;
    ctx.beginPath();
    ctx.arc(cx, cy, capR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = colours.line;
    ctx.stroke();

    // The indicator, and the spindle it turns on.
    ctx.strokeStyle = locked ? colours.signalStrong : colours.signal;
    ctx.lineWidth = 3;
    spoke(capR * 0.3, capR * 0.9, angle);

    ctx.fillStyle = colours.hub;
    ctx.beginPath();
    ctx.arc(cx, cy, capR * 0.16, 0, Math.PI * 2);
    ctx.fill();
  }

  const resizeObserver = new ResizeObserver(() => scheduleDraw());
  resizeObserver.observe(element);
  scheduleDraw();

  return {
    element,
    setValue: (next: number) => apply(next, false),
    ...(isEncoder
      ? {
          setStrength: (next: number): void => {
            strength = clamp(next, 0, 1);
            const nowLocked = strength >= 0.85;
            lock?.style.setProperty('--nb-knob-strength', strength.toFixed(3));
            if (lock) lock.dataset.locked = String(nowLocked);
            // Only a change of lock repaints the cap: strength arrives with every audio frame,
            // and the indicator has nothing else to say about it.
            if (nowLocked !== locked) {
              locked = nowLocked;
              element.setAttribute('aria-valuetext', valueText(value));
              scheduleDraw();
            }
          },
        }
      : {}),
    dispose: () => {
      resizeObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      if (commitTimer) clearTimeout(commitTimer);
      if (idleTimer) clearTimeout(idleTimer);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', endDrag);
      element.removeEventListener('pointercancel', endDrag);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('keydown', onKeyDown);
      element.removeEventListener('keyup', onKeyUp);
    },
  };

  /** Pots announce as a percentage — what a range input in their place reported. */
  function ariaValue(raw: number): number {
    return isEncoder ? raw : Math.round(raw * 100);
  }

  function valueText(raw: number): string {
    if (!isEncoder) return `${Math.round(raw * 100)}%`;
    return `${raw.toLocaleString('en-US').replace(/,/g, ' ')} kHz${
      locked ? ' — signal locked' : ''
    }`;
  }
}
